import type { ProcessingStatus } from "../api/types";

/* The words and the arithmetic behind the Processing screen
 * (docs/plans/processing-screen.md). Everything the screen shows is a fact
 * from the engine's views. What is here turns a fact into words in the
 * reader's time zone and a series of points into a line. No rate, no
 * expected finish and no state is decided here: those come from
 * api.processing_status and api.processing_worker (engine migration 164).
 *
 * Pure, so scripts/test_processing.mjs can check it. */

export type Window = "24h" | "7d" | "30d";
export const WINDOWS: { key: Window; label: string }[] = [
  { key: "24h", label: "24 hours" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
];

/* The window is a per-browser convenience, which is what localStorage is for.
   A private window, or blocked storage, falls back to 7 days. */
const WINDOW_KEY = "processing.window";
export function readWindow(): Window {
  try {
    const v = localStorage.getItem(WINDOW_KEY);
    return v === "24h" || v === "30d" ? v : "7d";
  } catch {
    return "7d";
  }
}
export function rememberWindow(w: Window): void {
  try { localStorage.setItem(WINDOW_KEY, w); } catch { /* storage is off, and the page works without it */ }
}

/* The order the engine walks a page through, and the order the kinds read in.
   Labels come from the API (lake.worker and lake.work_kind). These are the
   fallbacks while a row is still loading. */
export const STAGES: { key: string; label: string }[] = [
  { key: "lookup", label: "Look up" },
  { key: "triage", label: "Triage" },
  { key: "body", label: "Fetch page" },
  { key: "extraction", label: "Extract" },
  { key: "resolver", label: "Resolve" },
];
export const KIND_ORDER = ["person", "campaign", "review", "new_company", "known_only_by_name", "stage_check",
                           "feed", "other"];

/* A type of work's place in the screen's order. A type the engine adds
   before this list names it goes last rather than first, and is still shown:
   known_only_by_name (engine migration 263) was left out of both charts until
   2026-10-20. */
export function kindRank(k: string | null | undefined): number {
  const i = KIND_ORDER.indexOf(k ?? "");
  return i < 0 ? KIND_ORDER.length : i;
}

function inKindOrder(keys: Iterable<string>): string[] {
  return [...new Set(keys)].sort((a, b) => kindRank(a) - kindRank(b) || a.localeCompare(b));
}

export const PRIORITY_WORDS: Record<string, string> = {
  paused: "Paused", low: "Low", normal: "Normal", high: "High", first: "First",
};

/* A kind's priority as the row says it: "High", or "Paused until 5:00 PM
   tomorrow, then Low" when it was set until a time. The engine decides the
   priority it has now and what it returns to (api.processing_kind). */
export function priorityWords(priority: string | null | undefined, until: string | null | undefined,
                              after: string | null | undefined, now: Date = new Date()): string {
  const word = PRIORITY_WORDS[priority ?? ""] ?? priority ?? "";
  if (!until) return word;
  const then = PRIORITY_WORDS[after ?? ""] ?? after ?? "";
  return `${word} until ${whenWords(until, now)}${then ? `, then ${then}` : ""}`;
}

/* Whether a change to a kind's settings is still waiting for a stage to take
   it: some stage has not started a pass since the change. Every stage reads
   the priorities at the start of its pass (engine migration 168). */
export function changeWaiting(changedAt: string | null | undefined,
                              stageStarts: (string | null | undefined)[]): boolean {
  if (!changedAt) return false;
  const t = new Date(changedAt).getTime();
  if (Number.isNaN(t)) return false;
  return stageStarts.some((s) => !s || new Date(s).getTime() < t);
}

/* One color per kind, the same on every chart and legend. Chosen apart in hue
   and all darker than the page, so a legend square reads against white. */
export const KIND_COLORS: Record<string, string> = {
  person: "#2f6b3f", campaign: "#1f5fa8", review: "#7a5c14",
  new_company: "#7b3f8a", known_only_by_name: "#8a4b2a", stage_check: "#1f6f6a", feed: "#555555",
  other: "#9a9a9a",
};

const SERVICE_WORDS: Record<string, string> = {
  model: "The model that reads pages",
  search: "Web search",
  apollo: "Apollo",
  model_budget: "The daily limit on model calls",
};

/* What api.processing_series and api.processing_sample return. They are
   functions, so the generated types give them no row shape. A series row has
   its points as [time, value] pairs, one row per series, because a row per
   point ran past the API's 1,000-row limit and lost the series sorted last
   (engine migration 166). */
export type SeriesRow = {
  series: string; worker: string | null; work_kind: string | null;
  points: [string, number | string | null][] | null;
};
export type SampleRow = {
  id: string; title: string | null; url: string | null; page_kind: string | null;
  about_type: string | null; about_id: string | null; about_label: string | null;
  reason: string | null; arrived_at: string | null;
};
export type Point = { at: string; value: number };

/* What api.processing_window returns (engine migration 179): one row per
   type of work and one with a null work_kind for the total, over the window. */
export type WindowRow = {
  work_kind: string | null; calls: number | string | null;
  spend_usd: number | string | null; added: number | string | null;
};

/* The window in words, for a sentence: "the last 7 days". */
export function windowLabel(w: Window): string {
  return w === "24h" ? "the last 24 hours" : w === "30d" ? "the last 30 days" : "the last 7 days";
}

/* One type's totals over the window, or the whole with a null type. Absent
   rows read as nothing rather than zero, so a page still loading says so. */
export function windowTotal(rows: WindowRow[] | undefined, kind: string | null):
  { calls: number; spend: number; added: number } | null {
  const row = (rows ?? []).find((r) => (r.work_kind ?? null) === kind);
  if (!row) return null;
  return { calls: Number(row.calls ?? 0), spend: Number(row.spend_usd ?? 0), added: Number(row.added ?? 0) };
}
/* What api.processing_passes returns: a worker's last 24 passes. */
export type PassRow = {
  started_at: string | null; finished_at: string | null; taken: number | null; outcomes: unknown;
  error: string | null; concurrency: number | null; batch_limit: number | null;
};

/* A time as a datetime-local input wants it, in the reader's clock:
   "2026-10-07T17:00". The input has no time zone, so the browser reads its value as local time. */
export function localInputValue(iso: string | null | undefined): string {
  if (!iso || iso === "infinity") return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/* A datetime-local input's value back to an instant, or null when empty. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/* The parts of a pace a person changed, as the setter takes them: a part left
   as it was goes as null, which the engine reads as unchanged (migration 170).
   every is typed in minutes and kept in seconds. */
export function paceChanges(
  row: { batch?: number | null; every_seconds?: number | null; concurrency?: number | null; paused?: boolean | null },
  typed: { batch: string; everyMinutes: string; concurrency: string; paused: boolean },
): { p_batch: number | null; p_every_seconds: number | null; p_concurrency: number | null; p_paused: boolean | null } {
  const batch = typed.batch.trim() === "" ? null : Number(typed.batch);
  const every = typed.everyMinutes.trim() === "" ? null : Math.round(Number(typed.everyMinutes) * 60);
  const conc = typed.concurrency.trim() === "" ? null : Number(typed.concurrency);
  return {
    p_batch: batch !== null && batch !== row.batch ? batch : null,
    p_every_seconds: every !== null && every !== row.every_seconds ? every : null,
    p_concurrency: conc !== null && conc !== row.concurrency ? conc : null,
    p_paused: typed.paused !== !!row.paused ? typed.paused : null,
  };
}

/* Whole dollars, everywhere money is shown. It showed cents below a hundred
   dollars until 2026-10-08, when James found a column that mixed $19.12 with
   $401 confusing. The one figure that needs cents, spend per record, uses
   cents() down its whole column instead. */
export function money(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === "") return "";
  const v = Number(n);
  if (Number.isNaN(v)) return "";
  return "$" + Math.round(v).toLocaleString();
}

/* Dollars and cents, always two places, for a column of small figures. */
export function cents(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === "") return "";
  const v = Number(n);
  if (Number.isNaN(v)) return "";
  return "$" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function everyWords(seconds: number | null | undefined): string {
  if (!seconds) return "";
  if (seconds % 604800 === 0) return seconds === 604800 ? "weekly" : `every ${seconds / 604800} weeks`;
  if (seconds % 86400 === 0) return seconds === 86400 ? "daily" : `every ${seconds / 86400} days`;
  if (seconds % 3600 === 0) return `every ${seconds / 3600} h`;
  if (seconds % 60 === 0) return `every ${seconds / 60} min`;
  return `every ${seconds} s`;
}

/* "600 a pass, every 10 min, 8 at once", from a worker's row. */
export function paceWords(w: {
  batch?: number | null; every_seconds?: number | null; concurrency?: number | null; shape?: string | null;
}): string {
  const parts: string[] = [];
  if (w.batch) parts.push(`${w.batch.toLocaleString()} a pass`);
  const every = everyWords(w.every_seconds);
  if (every) parts.push(every);
  if (w.shape !== "scheduled" && w.concurrency) {
    parts.push(w.concurrency === 1 ? "one at a time" : `${w.concurrency} at once`);
  }
  return parts.join(", ");
}

/* A time in the reader's own clock: today's as a time, tomorrow's as
   "tomorrow 3:00 AM", anything else with its date. */
export function localTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (d.toDateString() === now.toDateString()) return time;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return `tomorrow ${time}`;
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${time}`;
}

/* A time inside a sentence: "5:00 PM" today, "5:00 PM tomorrow", or
   "Oct 8, 5:00 PM". localTime puts "tomorrow" first, which reads well in a
   table cell and wrong after "by the reset at". */
export function whenWords(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (d.toDateString() === now.toDateString()) return time;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return `${time} tomorrow`;
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${time}`;
}

/* A UTC day as the chart labels it. The charts' days are UTC because the
   limit counts a UTC day, so a day labeled in the reader's clock would read a
   day early in Phoenix. */
export function utcDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

/* The hour's change as words, for a panel: "3,327 fewer than an hour ago". */
export function changeSentence(n: number | null | undefined): string {
  if (n === null || n === undefined) return "";
  if (n === 0) return "the same as an hour ago";
  return `${Math.abs(n).toLocaleString()} ${n > 0 ? "more" : "fewer"} than an hour ago`;
}

/* How long ago, for a pass that ran minutes or hours ago. Beyond a day the
   date itself says more. */
export function agoWords(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const s = Math.round((now.getTime() - d.getTime()) / 1000);
  if (s < 0) return localTime(iso, now);
  if (s < 90) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 36) return `${h} h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function clearsWords(state: string | null | undefined, at: string | null | undefined): string {
  switch (state) {
    case "now": return "Now";
    case "paused": return "Paused";
    // Triage waiting so Extract can use the rest of the limit (engine
    // migration 174).
    case "held": return "Waiting for Extract";
    case "next_pass": return "Next pass";
    case "not_at_this_pace": return "Not at this pace";
    case "not_measured": return "Not measured yet";
    case "at": return at ? localTime(at) : "";
    default: return "";
  }
}

export function changeWords(n: number | null | undefined): string {
  if (n === null || n === undefined || n === 0) return "";
  return `${n > 0 ? "+" : ""}${n.toLocaleString()}/h`;
}

export function durationWords(start?: string | null, end?: string | null): string {
  if (!start || !end) return "";
  const s = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000);
  if (Number.isNaN(s) || s < 0) return "";
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return r ? `${m} min ${r} s` : `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

/* A pass's outcomes, "ok 243, empty 27, refused 27", from the jsonb the
   worker wrote. Zero counts are left out, since a pass that fetched 300
   pages with no error is three words rather than eight. */
export function outcomeWords(v: unknown): string {
  if (!v || typeof v !== "object" || Array.isArray(v)) return "";
  return Object.entries(v as Record<string, unknown>)
    // "in" and "out" are a model-calling pass's token totals, not outcomes.
    .filter(([k, n]) => typeof n === "number" && n !== 0 && k !== "in" && k !== "out")
    // A Triage pass that waited for Extract records it as a flag, not a count
    // (engine migration 174).
    .map(([k, n]) => k === "waited_for_extract" ? "waited for Extract"
      : `${k.replace(/_/g, " ")} ${(n as number).toLocaleString()}`)
    .join(", ");
}

/* A pass's mix by type of work, from outcomes.by_kind (engine migration 168):
   "Campaigns 75, New companies EVIE found 75". Shows what a priority did. */
export function kindMixWords(v: unknown, labels: Record<string, string>): string {
  if (!v || typeof v !== "object" || Array.isArray(v)) return "";
  const mix = (v as Record<string, unknown>).by_kind;
  if (!mix || typeof mix !== "object" || Array.isArray(mix)) return "";
  return inKindOrder(Object.keys(mix))
    .filter((k) => typeof (mix as Record<string, unknown>)[k] === "number")
    .map((k) => `${labels[k] ?? k} ${((mix as Record<string, number>)[k] ?? 0).toLocaleString()}`)
    .join(", ");
}

/* Until when model calls are paused: a time, or until someone resumes them,
   which the engine keeps as infinity (engine migration 171). */
export function pausedUntilWords(until: string | null | undefined, now: Date = new Date()): string {
  if (!until) return "";
  if (until === "infinity") return "until someone resumes them";
  return `until ${whenWords(until, now)}`;
}

/* The one sentence at the top of the page. The state and every figure in it
   come from api.processing_status. Stopped is every reader stopped. Held is
   part of the work stopped while the rest runs, and a clog elsewhere is said
   after it, because a hold does not stop work piling up somewhere else. */
export function statusSentence(s: ProcessingStatus, now: Date = new Date()): string {
  if (s.state === "stopped") {
    if (s.stopped_by === "pause") {
      const until = pausedUntilWords(s.paused_until, now);
      return `Stopped. Model calls are paused${until ? ` ${until}` : ""}${s.paused_by ? `, by ${s.paused_by}` : ""}.`;
    }
    if (s.stopped_by === "limit") {
      return `Stopped. The daily limit on model calls was reached`
        + `${s.stopped_since ? ` at ${whenWords(s.stopped_since, now)}` : ""}`
        + ` and resets at ${s.resets_at ? whenWords(s.resets_at, now) : "midnight UTC"}.`;
    }
    const service = SERVICE_WORDS[s.stopped_service ?? ""] ?? s.stopped_service ?? "A service";
    return `Stopped. ${service} has not answered`
      + `${s.stopped_since ? ` since ${whenWords(s.stopped_since, now)}` : ""}.`;
  }
  const clog = s.clog_worker
    ? `Clogged at ${s.clog_label ?? s.clog_worker}. `
      + `${num(s.clog_waiting)} waiting, ${num(s.clog_out_per_hour)} an hour out, ${num(s.clog_in_per_hour)} an hour in.`
    : "";
  if (s.state === "held" && s.held_by === "reserve") {
    return `Bulk work stopped at the reserve. Types set to First still run, Sent by a person among them`
      + `${s.resets_at ? ` until the limit resets at ${whenWords(s.resets_at, now)}` : ""}.`
      + (clog ? ` ${clog}` : "");
  }
  if (s.state === "held" && s.held_by === "extract") {
    // The engine's own sentence, with Extract's claim and what is left
    // (engine migration 174).
    return (s.held_reason ?? "Triage is waiting so Extract can finish pages.")
      + (clog ? ` ${clog}` : "");
  }
  if (s.state === "held") {
    const service = SERVICE_WORDS[s.held_service ?? ""] ?? s.held_service ?? "A service";
    const stage = STAGES.find((st) => st.key === s.held_worker)?.label ?? s.held_worker ?? "A stage";
    return `${stage} stopped. ${service} has not answered`
      + `${s.held_since ? ` since ${whenWords(s.held_since, now)}` : ""}. The other stages are running.`
      + (clog ? ` ${clog}` : "");
  }
  if (s.state === "clogged" && clog) return clog;
  return "Flowing. No stage is clogged.";
}

/* The sentence under the limit bar. */
export function limitSentence(s: ProcessingStatus, now: Date = new Date()): string {
  if (s.cap_usd === null || s.cap_usd === undefined) {
    return `${money(s.spent_today)} spent today. No daily limit is set.`;
  }
  const head = `${money(s.spent_today)} of ${money(s.cap_usd)}.`;
  const reserve = Number(s.reserve_usd ?? 0);
  const kept = reserve > 0 ? ` ${money(reserve)} is kept for types set to First.` : "";
  if (s.paused_until) {
    return `${head} Model calls are paused ${pausedUntilWords(s.paused_until, now)}.`;
  }
  if (s.state === "stopped" && s.stopped_by === "limit") {
    return `${head} Model calls resume at ${s.resets_at ? whenWords(s.resets_at, now) : "midnight UTC"}.`;
  }
  if (s.state === "held" && s.held_by === "reserve") {
    return `${head} Bulk work stopped at the reserve, and types set to First can use the last ${money(reserve)}.`;
  }
  if (s.reserve_reached_at) {
    return `${head} At this pace, bulk work stops at the reserve around ${whenWords(s.reserve_reached_at, now)}.${kept}`;
  }
  if (s.cap_reached_at) {
    return `${head} At this pace, the limit is reached around ${whenWords(s.cap_reached_at, now)}.${kept}`;
  }
  if (s.projected_at_reset !== null && s.projected_at_reset !== undefined && s.resets_at) {
    return `${head} At this pace, ${money(s.projected_at_reset)} by the reset at ${whenWords(s.resets_at, now)}.${kept}`;
  }
  return `${head}${kept}`;
}

function num(n: number | null | undefined): string {
  return n === null || n === undefined ? "" : n.toLocaleString();
}

/* One series out of the series rows, as points in time order. A null worker
   or kind matches a null in the row, which is how a stage's total and the
   spend total are asked for. */
export function seriesFor(rows: SeriesRow[], series: string, worker: string | null, kind: string | null): Point[] {
  const row = rows.find((r) => r.series === series && (r.worker ?? null) === worker && (r.work_kind ?? null) === kind);
  if (!row || !Array.isArray(row.points)) return [];
  return row.points
    .map(([at, value]) => ({ at, value: Number(value ?? 0) }))
    .sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
}

/* The kinds a series has rows for, in the screen's order. */
export function kindsIn(rows: SeriesRow[], series: string): string[] {
  return inKindOrder(rows.filter((r) => r.series === series && r.work_kind).map((r) => r.work_kind as string));
}

/* A polyline through the points, scaled to the box. `top` lets several lines
   share one scale, so two sparklines in a row can be compared. */
export function sparkPath(points: Point[], width: number, height: number, top?: number): { d: string; min: number; max: number } {
  if (points.length < 2) return { d: "", min: 0, max: 0 };
  const values = points.map((p) => p.value);
  const lo = Math.min(...values);
  const hi = Math.max(top ?? Number.NEGATIVE_INFINITY, ...values);
  const span = hi - lo || 1;
  const t0 = new Date(points[0]?.at ?? 0).getTime();
  const t1 = new Date(points.at(-1)?.at ?? 0).getTime();
  const tspan = t1 - t0 || 1;
  const d = points.map((p, i) => {
    const x = ((new Date(p.at).getTime() - t0) / tspan) * (width - 2) + 1;
    const y = height - 1 - ((p.value - lo) / span) * (height - 2);
    return `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  return { d, min: lo, max: hi };
}

/* Hourly points summed into UTC days, for the daily charts. The API's hours
   are in UTC, and the limit is a UTC day, so the days are UTC too. */
export function byDay(points: Point[]): Point[] {
  const sums = new Map<string, number>();
  for (const p of points) {
    const day = new Date(p.at).toISOString().slice(0, 10);
    sums.set(day, (sums.get(day) ?? 0) + p.value);
  }
  return [...sums.entries()].sort().map(([day, value]) => ({ at: `${day}T00:00:00Z`, value }));
}

/* The reasons a kind's lookups are queued, most first, from the jsonb the
   view gives. */
export function reasonLines(v: unknown): { reason: string; n: number }[] {
  if (!v || typeof v !== "object" || Array.isArray(v)) return [];
  return Object.entries(v as Record<string, unknown>)
    .filter(([, n]) => typeof n === "number")
    .map(([reason, n]) => ({ reason, n: n as number }))
    .sort((a, b) => b.n - a.n);
}

/* A recorded setting's value as its control says it, by the value_type the
   engine gives each change (api.setting_change, engine migration 176). */
export function changeValueWords(type: string | null | undefined, v: unknown, now: Date = new Date()): string {
  if (v === null || v === undefined) return type === "time" ? "none" : "not set";
  switch (type) {
    case "money": return money(v as number | string);
    case "time": return v === "infinity" ? "until resumed" : whenWords(String(v), now);
    case "onoff": return v === true ? "On" : v === false ? "Off" : String(v);
    case "yesno": return v === true ? "Yes" : v === false ? "No" : String(v);
    case "priority": return PRIORITY_WORDS[String(v)] ?? String(v);
    case "seconds": return everyWords(Number(v)).replace(/^every /, "");
    case "number": return Number(v).toLocaleString();
    default: return typeof v === "string" ? v : JSON.stringify(v);
  }
}

/* A change from old to new: "Normal to First until 5:00 PM tomorrow". */
export function changeFromToWords(
  row: { value_type?: string | null; old_value?: unknown; new_value?: unknown; until_value?: unknown },
  now: Date = new Date(),
): string {
  const until = row.until_value ? ` until ${changeValueWords("time", row.until_value, now)}` : "";
  return `${changeValueWords(row.value_type, row.old_value, now)} to ${changeValueWords(row.value_type, row.new_value, now)}${until}`;
}

/* A change's effect: the engine's sentence once the hour after it has
   passed, "Measuring, ready at 4:20 PM" before that, and nothing for a
   change with nothing to measure. */
export function effectWords(effect: string | null | undefined, readyAt: string | null | undefined,
                            now: Date = new Date()): string {
  if (effect) return effect;
  if (readyAt && new Date(readyAt).getTime() > now.getTime()) return `Measuring, ready at ${whenWords(readyAt, now)}`;
  return "";
}

/* One scheduled job on the shared timeline (api.processing_schedule, engine
   migration 227). Every job has the same cells, an hour each on the 24-hour
   window and a UTC day on 7 and 30, so rows line up and a moment that failed
   several jobs reads as one column. A cell is [starts, state, runs, failed].
   The engine decides health, lateness and why a job is off. This file only
   says them in words. */
export type ScheduleRow = {
  worker: string | null; label: string | null; about: string | null; every_seconds: number | null;
  off_reason: string | null; late: boolean | null; health: string | null; runs: number | null;
  failed: number | null; bucket_seconds: number | null; buckets: [string, string, number, number][] | null;
};

export const CELL_WORDS: Record<string, string> = {
  work: "did work", ran: "ran clean", nothing: "nothing to do", failed: "failed",
  running: "running", missed: "missed a run", none: "not due",
};

const HEALTH_ORDER: Record<string, number> = { failing: 0, late: 1, failed_some: 2, ok: 3, off: 4 };

/* Problems first (failing, late, a failure in the window), then the rest
   from the most frequent to the least, then by name. An off job goes last,
   because it is off on purpose. */
export function scheduleOrder(a: ScheduleRow, b: ScheduleRow): number {
  const ha = HEALTH_ORDER[a.health ?? "ok"] ?? 3, hb = HEALTH_ORDER[b.health ?? "ok"] ?? 3;
  const pa = ha < 3, pb = hb < 3;
  if (pa !== pb) return pa ? -1 : 1;
  if (pa && ha !== hb) return ha - hb;
  if ((a.health === "off") !== (b.health === "off")) return a.health === "off" ? 1 : -1;
  const ea = a.every_seconds ?? 0, eb = b.every_seconds ?? 0;
  if (ea !== eb) return ea - eb;
  return (a.label ?? "").localeCompare(b.label ?? "");
}

export function needsALook(r: ScheduleRow): boolean {
  return r.health === "failing" || r.health === "late" || r.health === "failed_some";
}

/* The one word at the end of a row. */
export function healthWords(r: ScheduleRow): string {
  switch (r.health) {
    case "failing": return "Failing";
    case "late": return "Late";
    case "failed_some": return `${(r.failed ?? 0).toLocaleString()} of ${(r.runs ?? 0).toLocaleString()} failed`;
    case "off": return "Off";
    default: return "OK";
  }
}

function times(n: number): string {
  return n === 1 ? "once" : n === 2 ? "twice" : `${n.toLocaleString()} times`;
}

function listWords(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/* The sentence above the table, so most days it is all anybody reads:
   "15 jobs. Feed discovery and Processing meter each failed once in the
   last 7 days. Email is off until email sending is turned on." */
export function scheduleSummary(rows: ScheduleRow[], windowWords: string): string {
  if (!rows.length) return "No scheduled jobs.";
  const name = (r: ScheduleRow) => r.label ?? r.worker ?? "a job";
  const out: string[] = [`${rows.length} ${rows.length === 1 ? "job" : "jobs"}.`];
  const failing = rows.filter((r) => r.health === "failing").map(name);
  const late = rows.filter((r) => r.health === "late").map(name);
  const some = rows.filter((r) => r.health === "failed_some");
  if (failing.length) out.push(`${listWords(failing)} ${failing.length === 1 ? "is" : "are"} failing.`);
  if (late.length) out.push(`${listWords(late)} ${late.length === 1 ? "has" : "have"} not run on schedule.`);
  if (some.length) {
    const counts = new Set(some.map((r) => r.failed ?? 0));
    out.push(counts.size === 1 && some.length > 1
      ? `${listWords(some.map(name))} each failed ${times(some[0]?.failed ?? 0)} in ${windowWords}.`
      : `${listWords(some.map((r) => `${name(r)} failed ${times(r.failed ?? 0)}`))} in ${windowWords}.`);
  }
  if (!failing.length && !late.length && !some.length) {
    const on = rows.filter((r) => r.health !== "off").length;
    if (on === rows.length) out.push(`Every job ran on schedule with no failures in ${windowWords}.`);
    else if (on > 0) out.push(`The ${on} that are on ran on schedule with no failures in ${windowWords}.`);
  }
  for (const r of rows.filter((x) => x.health === "off")) {
    // "Email is off until email sending is turned on.", "Watchdog is paused."
    out.push(`${name(r)} is ${r.off_reason ? `${r.off_reason.charAt(0).toLowerCase()}${r.off_reason.slice(1)}` : "off"}.`);
  }
  return out.join(" ");
}

/* A cell's words, for its title and for a screen reader: "Oct 9: 2 runs,
   1 failed", "3 PM: 12 runs", "Oct 7: not due". */
export function cellWords(cell: [string, string, number, number], hourly: boolean): string {
  const [at, state, runs, failed] = cell;
  const when = hourly ? new Date(at).toLocaleTimeString(undefined, { hour: "numeric" }) : utcDay(at);
  if (!runs) return `${when}: ${CELL_WORDS[state] ?? state}`;
  const parts = [`${runs.toLocaleString()} ${runs === 1 ? "run" : "runs"}`];
  if (failed) parts.push(`${failed.toLocaleString()} failed`);
  else parts.push(CELL_WORDS[state] ?? state);
  return `${when}: ${parts.join(", ")}`;
}

/* The top of a chart's scale: the smallest round number at or above the
   largest value, from 1, 2, 2.5 and 5 times a power of ten, so the axis reads
   $500 or $1,000 rather than $498. */
export function niceCeil(v: number): number {
  if (!(v > 0)) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  for (const f of [1, 2, 2.5, 5, 10]) {
    if (v <= f * mag + 1e-9) return f * mag;
  }
  return 10 * mag;
}

/* Label every nth column so labels never crowd: every column up to 12, then
   every second, third and so on. */
export function labelStep(columns: number, most: number = 12): number {
  return Math.max(1, Math.ceil(columns / most));
}
